import { Tab, TabPanel, TabPanels, Tabs, TabsList } from '@octano/global-ui';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';
import { useLocation, useHistory } from 'react-router-dom';
import { PathsLayouts } from '../../config/routes';
import Sections from './sections';
import Subjects from './subjects';
import SpecialtyAreas from './specialty-areas';
import Schools from './schools';
import Careers from './careers';

interface TabComponentProps {
  currentTab: string;
  onTabChange: (tabId: string | number) => void;
}

const TabComponent = memo(({ currentTab, onTabChange }: TabComponentProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'views.maintainers',
  });

  return (
    <Card className="mt-5">
      <Tabs defaultTab={currentTab} onSelection={onTabChange}>
        <TabsList>
          <Tab key="specialtyAreas" id="specialtyAreas">
            {t('specialtyAreas.title')}
          </Tab>
          <Tab key="schools" id="schools">
            {t('schools.title')}
          </Tab>
          <Tab key="careers" id="careers">
            {t('careers.title')}
          </Tab>
          <Tab key="subjects" id="subjects">
            {t('subjects.title')}
          </Tab>
          <Tab key="sections" id="sections">
            {t('sections.title')}
          </Tab>
        </TabsList>
        <TabPanels>
          <TabPanel key="specialtyAreas" id="specialtyAreas">
            <SpecialtyAreas />
          </TabPanel>
          <TabPanel key="schools" id="schools">
            <Schools />
          </TabPanel>
          <TabPanel key="careers" id="careers">
            <Careers />
          </TabPanel>
          <TabPanel key="sections" id="sections">
            <Sections />
          </TabPanel>
          <TabPanel key="subjects" id="subjects">
            <Subjects />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Card>
  );
});

function AcademicMaintainer() {
  const location = useLocation();
  const history = useHistory();

  const getTabFromPath = useCallback(() => {
    const pathSegment = location.pathname.split('/').pop();
    return pathSegment && pathSegment !== 'academics'
      ? pathSegment
      : 'specialtyAreas';
  }, [location.pathname]);

  const [currentTab, setCurrentTab] = useState(getTabFromPath);

  const handleTabChange = useCallback(
    (tabId: string | number) => {
      if (tabId !== currentTab) {
        const routeToGo = `${PathsLayouts.maintainer}/academics/${tabId}`;
        history.replace(routeToGo);
        setCurrentTab(tabId as string);
      }
    },
    [currentTab, history],
  );

  useEffect(() => {
    setCurrentTab(getTabFromPath());
  }, [location.pathname, getTabFromPath]);

  return <TabComponent currentTab={currentTab} onTabChange={handleTabChange} />;
}

export default AcademicMaintainer;
